{#if enabled}
<div class={"assign-to " + assignToClass}>
    <span class={"assign-to-label " + assignToLabelClass}>{assignToLabelText}</span>
    <div class={"assign-to-field " + assignToFieldClass}>
        <span class={"assign-to-assignees " + assignToTextClass}>{assignToAssigneesText}</span>
        <PlainButton
            class={"assign-to-button " + assignToButtonClass}
            text={assingToButtonText}
            on:click={handleChangeButtonClick}
        />
    </div>

    {#if elementsHasBeenSubmittedAlert}
    <Alert
        heading = "You can't assign plan to participants when there are elements submitted";
        on:close={closeElementsHasBeenSubmittedAlert}
    />
    {/if}

    {#if elementsHasBeenAssignedToAlert}
    <Alert
        heading = "You can't assign plan to participants when there are elements that use assign-to";
        on:close={closeElementsHasBeenAssignedToAlert}
    />
    {/if}

    <AssignToModal
        bind:enabled={showModal}
        {isFirstLoad}
        {participants}
        {groups}
        {elements}
        on:assignToModalCancelled={handleCancel}
        on:assignToModalConfirmed={handleConfirmed}/>
</div>
{/if}

<script>
    import { formatLanguageString } from "@itslearning/atlas/strings/format";
    import i18n from "@its-ui/planner-common/i18n";
    import PlainButton from "@its-ui/planner-common/components/Button/PlainButton.svelte";
    import AssignToModal from "./Modal/AssignToModal.svelte";
    import { getAssignedPlanParticipants, getAssignedPlanGroups } from "../../api";
    import { onMount, createEventDispatcher } from "svelte";
    import Alert from "@itslearning/prometheus/assets/modals/Alert/v1/Alert.svelte";
    import config from "../../config";

    export let assignToClass = "plan-page__assign-to";
    export let assignToButtonClass = "plan-page__button";
    export let assingToButtonText = i18n.change;
    export let assignToFieldClass = "plan-page__field";
    export let assignToLabelClass = "plan-page__property-label";
    export let assignToLabelText = i18n.assignedTo;
    export let assignToTextClass = "plan-page__property-text";
    export let enabled = false;
    export let elements = null;

    let groupCount = 0;
    let groupName = "";
    let isFirstLoad = config.newPlan;
    let participantCount = 0;
    let showModal = false;
    let participants = null;
    let originalParticipants = null;
    let originalGroups = null;
    let groups = null;
    let elementDoneCount = 0;
    let participantsTotalCount = 0;
    let elementsHasBeenSubmittedAlert = false;
    let elementsHasBeenAssignedToAlert = false;

    const assignToTextMap = [
        { condition: (p, g) => p === 0 && g === 0, text: i18n.allParticipants },
        { condition: (p, g) => p === participantsTotalCount && g === 0, text: i18n.allParticipants },
        { condition: (p, g) => p === 1 && g === 0, text: i18n.assignedTo1Participant },
        { condition: (p, g) => p > 1 && g === 0, text: (p) => formatLanguageString(i18n.assignedToXParticipants, p) },
        { condition: (p, g) => p === 0 && g === 1, text: (p, g, n) => n },
        { condition: (p, g) => p === 0 && g > 1, text: (p, g) => formatLanguageString(i18n.assignedToXGroups, g) },
        { condition: (p, g) => p === 1 && g === 1, text: (p, g, n) => formatLanguageString(i18n.assignedTo1ParticipantY, n) },
        { condition: (p, g) => p === 1 && g > 1, text: (p, g) => formatLanguageString(i18n.assignedTo1ParticipantYGroups, g) },
        { condition: (p, g) => p > 1 && g === 1, text: (p, g, n) => formatLanguageString(i18n.assignedToXParticipantsY, p, n) },
        { condition: (p, g) => p > 1 && g > 1, text: (p, g) => formatLanguageString(i18n.assignedToXParticipantsYGroups, p, g) },
    ];

    $: assignToAssigneesText = getAssignToAssigneesText(participantCount, groupCount, groupName);

    onMount(async () => {
        if (!config.publicAccessKey) {
            participants = await getAssignedPlanParticipants();
            getParticipantsCount();
            getTotalParticipantsCount();

            groups = await getAssignedPlanGroups();
            getGroupsCountAndName();

            getAssignToAssigneesText(participantCount, groupCount, groupName);

            elementDoneCount = sumDoneCount(elements);

            setOriginalValues();
        }
    });

    const dispatch = createEventDispatcher();

    const getAssignToAssigneesText = (participantCount, groupCount, groupName) => {
        const entry = assignToTextMap.find(({ condition }) => condition(participantCount, groupCount));

        return entry ? (typeof entry.text === 'function' ? entry.text(participantCount, groupCount, groupName) : entry.text) : "";
    };

    const getParticipantsCount = () => {
        if (participants && Array.isArray(participants.entityArray)) {
            participantCount = participants.entityArray.filter((item) => item.isAssigned).length;
        }
    };

    const getTotalParticipantsCount = () => {
        if (participants && Array.isArray(participants.entityArray)) {
            participantsTotalCount = participants.entityArray.length;
        }
    };

    const getGroupsCountAndName = () => {
        if (groups && Array.isArray(groups.entityArray)) {
            groupCount = groups.entityArray.filter((item) => item.isAssigned).length;

            if (groupCount === 1) {
                const assignedGroup = groups.entityArray.find((item) => item.isAssigned);

                if (assignedGroup) {
                    groupName = assignedGroup.title;
                }
            }
        }
    };

    const handleChangeButtonClick = () => {
        const elementsWithAssignedTo = hasAnyElementAssignTo(elements, participants);

        if (elementDoneCount === 0 && !elementsWithAssignedTo) {
            showModal = true;
        } else {
            if (elementDoneCount !== 0) {
                elementsHasBeenSubmittedAlert = true;
            } else if (elementsWithAssignedTo) {
                elementsHasBeenAssignedToAlert = true;
            }
        }
    };

    const handleCancel = () => {
        if (originalParticipants && originalParticipants.entityArray) {
            participants = JSON.parse(JSON.stringify(originalParticipants));
        }

        if (originalGroups && originalGroups.entityArray) {
            groups = JSON.parse(JSON.stringify(originalGroups));
        }

        updateText();
    };

    const handleConfirmed = ({ detail: { updatedElements } }) => {
        elementDoneCount = sumDoneCount(elements);

        updateText();
        setOriginalValues();

        dispatch('assignToChanged', { updatedElements });
        isFirstLoad = false;
    };

    const updateText = () => {
        getParticipantsCount();
        getGroupsCountAndName();
        getAssignToAssigneesText(participantCount, groupCount, groupName);
    };

    const setOriginalValues = () => {
        if (participants && participants.entityArray) {
            originalParticipants = JSON.parse(JSON.stringify(participants));
        }

        if (groups && groups.entityArray) {
            originalGroups = JSON.parse(JSON.stringify(groups));
        }
    };

    // Function to sum doneCount
    const sumDoneCount = (elements) => {
        return elements.reduce((total, element) => total + element.doneCount, 0);
    };

    const hasAnyElementAssignTo = (elements, participants) => {
        const assignedParticipants = participants.entityArray.filter(item => item.isAssigned);
        const allAssigned = participants.entityArray.every(item => item.isAssigned);
        const noneAssigned = participants.entityArray.every(item => !item.isAssigned);

        if (allAssigned || noneAssigned) {
            return false;
        }

        if (elements.every(element => element.doneCount <= 0)) {
            return false;
        }

        return elements.some(element => element.totalCount !== assignedParticipants.length);
    };

    const closeElementsHasBeenSubmittedAlert = () => {
        elementsHasBeenSubmittedAlert = false;
    };

    const closeElementsHasBeenAssignedToAlert = () => {
        elementsHasBeenAssignedToAlert = false;
    };
</script>
